<template>
    <v-container class="py-12">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span id="title" class="page-title">Importar planilha</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
                    </div>
                    <div>
                        <v-btn v-if="preview" type="button" outlined color="primary" elevation="0" class="mr-2"
                            @click="reset">Colar outra</v-btn>
                        <v-btn type="button" elevation="0" color="primary" outlined to="/logistica">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- 1. Colar -->
        <v-row v-if="!preview">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="filter-card-title">Cole as linhas da planilha diária</v-card-title>
                    <v-card-text>
                        <p class="mb-4 grey--text text--darken-1">
                            Selecione no Excel os blocos que quer importar, incluindo a linha do horário
                            (ex.: <b>07:00 · EVENTUAL · ENTRADA</b>), a linha da empresa e o cabeçalho
                            (<b>DATA, NOME, ENDEREÇO...</b>), copie e cole abaixo. Pode colar o dia inteiro.
                        </p>
                        <v-row>
                            <v-col cols="12" sm="3">
                                <v-text-field dense outlined type="date" label="Data da viagem" v-model="form.date"
                                    hint="Usada quando a linha não tiver data" persistent-hint />
                            </v-col>
                        </v-row>
                        <v-textarea outlined v-model="form.text" rows="14" class="paste-area mt-2" hide-details
                            placeholder="Cole aqui (Ctrl+V)" />
                        <div class="d-flex align-center mt-4">
                            <span class="grey--text">{{ lineCount }} linhas coladas</span>
                            <v-spacer />
                            <v-btn color="primary" elevation="0" :loading="loading" :disabled="!form.text.trim()"
                                @click="read">Ler planilha</v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- 2. Revisar -->
        <template v-else>
            <v-row>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Passageiros" :value="rows.length" icon="mdi-account-multiple" clickable
                        @click="filter = 'all'" />
                </v-col>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Prontas" :value="counts.ok" icon="mdi-check-circle-outline" color="#2e7d32"
                        clickable @click="filter = 'ok'" />
                </v-col>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Conferir" :value="counts.warning" icon="mdi-alert-outline" color="#ef6c00"
                        clickable @click="filter = 'warning'" />
                </v-col>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Com problema" :value="counts.error" icon="mdi-close-circle-outline"
                        color="#c62828" clickable :alert="counts.error > 0" @click="filter = 'error'" />
                </v-col>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Já existentes" :value="counts.duplicate" icon="mdi-content-duplicate"
                        clickable @click="filter = 'duplicate'" />
                </v-col>
                <v-col cols="6" sm="4" md="2">
                    <StatCard label="Carros" :value="preview.summary.vehicles" icon="mdi-car-multiple"
                        info="Passageiros com o mesmo transporte no mesmo bloco da planilha viram o mesmo carro na Logística" />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-card-text>
                            <div class="d-flex align-center flex-wrap mb-2">
                                <v-btn-toggle v-model="filter" mandatory dense color="primary" class="mr-4">
                                    <v-btn small value="all">Todas</v-btn>
                                    <v-btn small value="error">Com problema</v-btn>
                                    <v-btn small value="warning">Conferir</v-btn>
                                    <v-btn small value="ok">Prontas</v-btn>
                                    <v-btn small value="duplicate">Já existentes</v-btn>
                                </v-btn-toggle>
                                <v-text-field v-model="search" dense outlined hide-details clearable
                                    prepend-inner-icon="mdi-magnify" placeholder="Buscar passageiro ou empresa"
                                    style="max-width: 300px" />
                                <v-spacer />
                                <span class="mr-4"><b>{{ selectedRows.length }}</b> selecionadas</span>
                                <v-btn color="primary" elevation="0" :loading="saving" :disabled="!selectedRows.length"
                                    @click="confirmCreate">Criar {{ selectedRows.length }} solicitações</v-btn>
                            </div>

                            <v-data-table :headers="headers" :items="visibleRows" item-key="key" group-by="block"
                                disable-pagination hide-default-footer disable-sort class="app-table import-table"
                                no-data-text="Nenhuma linha neste filtro">

                                <template v-slot:[`group.header`]="{ group, headers: cols }">
                                    <td :colspan="cols.length" class="block-header">
                                        <template v-if="blockOf(group)">
                                            <v-checkbox class="d-inline-flex mt-0 pt-0 mr-1" hide-details dense
                                                :input-value="blockSelected(group)" :indeterminate="blockPartial(group)"
                                                @change="selectBlock(group, $event)" />
                                            <strong>{{ blockOf(group).time }}</strong>
                                            <v-select class="d-inline-flex block-type mx-2" dense hide-details outlined
                                                :items="types" item-text="name" item-value="id"
                                                :value="blockType(group)" @change="setBlockType(group, $event)" />
                                            <strong>{{ blockOf(group).customer }}</strong>
                                            <span v-if="blockOf(group).itinerary" class="ml-2">· {{ blockOf(group).itinerary }}</span>
                                            <span v-if="blockOf(group).requester" class="ml-2 grey--text">· Solicitante: {{ blockOf(group).requester }}</span>
                                            <span v-if="blockOf(group).label" class="ml-2 grey--text">· {{ blockOf(group).label }}</span>
                                            <div v-if="blockOf(group).notes.length" class="block-notes">
                                                <v-icon x-small left>mdi-note-text-outline</v-icon>{{ blockOf(group).notes.join(' · ') }}
                                            </div>
                                        </template>
                                    </td>
                                </template>

                                <template v-slot:[`item.selected`]="{ item }">
                                    <v-checkbox v-model="item.selected" hide-details dense class="mt-0 pt-0"
                                        :disabled="problems(item).length > 0" />
                                </template>

                                <template v-slot:[`item.passenger`]="{ item }">
                                    <div class="py-2" style="min-width: 220px; max-width: 300px">
                                        <div class="text-caption grey--text">Planilha: {{ item.sheet_name }}</div>
                                        <div v-if="item.passenger_id && !item.editingPassenger" class="d-flex align-center">
                                            <strong>{{ item.passenger_name }}</strong>
                                            <v-icon small class="ml-1" @click="editPassenger(item)">mdi-pencil-outline</v-icon>
                                        </div>
                                        <v-select v-else-if="item.candidates.length && !item.editingPassenger" dense outlined
                                            hide-details :items="item.candidates" item-text="name" item-value="id"
                                            placeholder="Escolha o passageiro" @change="pickCandidate(item, $event)">
                                            <template v-slot:item="{ item: c }">
                                                <div>
                                                    <div>{{ c.name }}</div>
                                                    <small class="grey--text">{{ customerName(c.customer_id) }} · {{ c.address || 'sem endereço' }}</small>
                                                </div>
                                            </template>
                                        </v-select>
                                        <v-autocomplete v-else dense outlined hide-details return-object no-filter
                                            :items="item.searchResults || []" item-text="name" item-value="id"
                                            :loading="item.searching" placeholder="Buscar no cadastro"
                                            :search-input.sync="item.searchText"
                                            @update:search-input="searchPassenger(item, $event)"
                                            @change="pickPassenger(item, $event)" no-data-text="Digite o nome">
                                            <template v-slot:item="{ item: p }">
                                                <div>
                                                    <div>{{ p.name }}</div>
                                                    <small class="grey--text">{{ p.customer ? p.customer.name : '' }} · {{ p.address || 'sem endereço' }}</small>
                                                </div>
                                            </template>
                                        </v-autocomplete>
                                        <div v-if="item.sheet_address" class="text-caption grey--text mt-1"
                                            :title="item.passenger_address ? 'Cadastro: ' + item.passenger_address : ''">
                                            <v-icon x-small>mdi-map-marker-outline</v-icon> {{ item.sheet_address }}
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:[`item.company`]="{ item }">
                                    <div style="min-width: 150px; max-width: 220px" class="py-2">
                                        <div>{{ item.customer_name || '-' }}</div>
                                        <v-select v-if="addressesOf(item).length > 1" dense outlined hide-details class="mt-1"
                                            :items="addressesOf(item)" item-text="name" item-value="id"
                                            v-model="item.customer_address_id" placeholder="Endereço da empresa" />
                                        <small v-else-if="addressesOf(item).length" class="grey--text">{{ addressesOf(item)[0].name }}</small>
                                    </div>
                                </template>

                                <template v-slot:[`item.time`]="{ item }">
                                    <v-text-field v-model="item.time" type="time" dense outlined hide-details
                                        style="width: 110px" />
                                </template>

                                <template v-slot:[`item.vehicle`]="{ item }">
                                    <div style="width: 110px" class="py-2">
                                        <v-text-field v-model.number="item.vehicle_number" type="number" dense outlined
                                            hide-details style="width: 80px" placeholder="Carro" />
                                        <small v-if="item.transport" class="grey--text">{{ item.transport }}</small>
                                    </div>
                                </template>

                                <template v-slot:[`item.status`]="{ item }">
                                    <div class="py-2" style="min-width: 180px">
                                        <div v-for="msg in problems(item)" :key="'p' + msg" class="status-line red--text text--darken-2">
                                            <v-icon x-small color="red darken-2">mdi-close-circle</v-icon> {{ msg }}
                                        </div>
                                        <div v-for="msg in item.warnings" :key="'w' + msg" class="status-line orange--text text--darken-3">
                                            <v-icon x-small color="orange darken-3">mdi-alert</v-icon> {{ msg }}
                                        </div>
                                        <div v-if="!problems(item).length && !item.warnings.length" class="status-line green--text text--darken-2">
                                            <v-icon x-small color="green darken-2">mdi-check-circle</v-icon> Pronta
                                        </div>
                                        <div v-if="item.observation" class="text-caption grey--text mt-1">{{ item.observation }}</div>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <!-- Resultado -->
        <v-dialog v-model="result.show" max-width="560" persistent>
            <v-card>
                <v-card-title>Importação concluída</v-card-title>
                <v-card-text>
                    <p><b>{{ result.created }}</b> {{ result.created === 1 ? 'solicitação criada' : 'solicitações criadas' }}. Elas já aparecem na Logística com o carro
                        sugerido; confira, gere os horários e salve para criar as OS.</p>
                    <template v-if="result.errors.length">
                        <p class="red--text text--darken-2 mb-1"><b>{{ result.errors.length }}</b> {{ result.errors.length === 1 ? 'não foi criada' : 'não foram criadas' }}:</p>
                        <ul>
                            <li v-for="error in result.errors" :key="error.key">{{ error.name }}: {{ error.message }}</li>
                        </ul>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="result.show = false">Continuar aqui</v-btn>
                    <v-btn color="primary" elevation="0" to="/logistica">Ir para a Logística</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import debounce from 'lodash/debounce'

import StatCard from '@/layouts/components/StatCard.vue'

// Importação das viagens coladas da planilha diária: lê, revisa e cria as
// solicitações aprovadas que seguem para a Logística.
export default {
    name: 'LogisticsImport',

    components: { StatCard },

    data: () => ({
        breadcrumbs: [
            { text: 'Home', disabled: false, to: '/home', exact: true },
            { text: 'Logística', disabled: false, to: '/logistica', exact: true },
            { text: 'Importar planilha', disabled: true, exact: true }
        ],
        headers: [
            { text: '', value: 'selected', width: 40 },
            { text: 'Passageiro', value: 'passenger' },
            { text: 'Empresa', value: 'company' },
            { text: 'Horário', value: 'time' },
            { text: 'Carro', value: 'vehicle' },
            { text: 'Situação', value: 'status' }
        ],
        types: [
            { id: 1, name: 'ENTRADA' },
            { id: 2, name: 'SAÍDA' },
            { id: 3, name: 'PERSONALIZADO' }
        ],
        form: { date: moment().format('YYYY-MM-DD'), text: '' },
        loading: false,
        saving: false,
        preview: null,
        rows: [],
        customers: {},
        filter: 'all',
        search: '',
        result: { show: false, created: 0, errors: [] }
    }),

    computed: {
        lineCount() {
            return this.form.text ? this.form.text.split('\n').filter((line) => line.trim()).length : 0
        },

        blocksByIndex() {
            return new Map((this.preview ? this.preview.blocks : []).map((block) => [block.index, block]))
        },

        counts() {
            const counts = { ok: 0, warning: 0, error: 0, duplicate: 0 }
            for (const row of this.rows) {
                counts[this.statusOf(row)]++
                if (row.duplicate) counts.duplicate++
            }
            return counts
        },

        visibleRows() {
            const term = (this.search || '').trim().toLowerCase()

            return this.rows.filter((row) => {
                if (this.filter === 'duplicate' && !row.duplicate) return false
                if (['ok', 'warning', 'error'].includes(this.filter) && this.statusOf(row) !== this.filter) return false
                if (!term) return true

                return [row.sheet_name, row.passenger_name, row.customer_name].some((text) => (text || '').toLowerCase().includes(term))
            })
        },

        selectedRows() {
            return this.rows.filter((row) => row.selected && !this.problems(row).length)
        }
    },

    methods: {
        async read() {
            this.loading = true
            try {
                const response = await axios.post('/api/v2/logistics/import/preview', { text: this.form.text, date: this.form.date })
                const content = response.data.content

                this.customers = content.customers
                this.rows = content.rows.map((row) => ({
                    ...row,
                    editingPassenger: false,
                    searchText: null,
                    searchResults: [],
                    searching: false
                }))
                this.preview = content
                this.filter = content.summary.error ? 'error' : 'all'
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
            } finally {
                this.loading = false
            }
        },

        reset() {
            this.preview = null
            this.rows = []
            this.filter = 'all'
        },

        // ==== Situação da linha =============================================

        problems(row) {
            const list = []
            if (!row.date) list.push('Sem data')
            if (!row.time) list.push('Sem horário')
            if (Number(row.type) === 3) list.push('Personalizado: crie pela tela de OS')
            else if (![1, 2].includes(Number(row.type))) list.push('Escolha entrada ou saída no bloco')
            if (!row.passenger_id) list.push(row.candidates.length ? 'Escolha o passageiro' : 'Passageiro não encontrado no cadastro')
            else if (!row.passenger_has_location) list.push('Passageiro sem endereço no cadastro')
            if (row.passenger_id && !row.customer_address_id) list.push('Empresa sem endereço cadastrado')
            return list
        },

        statusOf(row) {
            if (this.problems(row).length) return 'error'
            return row.warnings.length ? 'warning' : 'ok'
        },

        // ==== Blocos ========================================================

        blockOf(index) {
            return this.blocksByIndex.get(Number(index))
        },

        blockRows(index) {
            return this.rows.filter((row) => Number(row.block) === Number(index))
        },

        blockType(index) {
            const rows = this.blockRows(index)
            return rows.length ? rows[0].type : null
        },

        setBlockType(index, type) {
            this.blockRows(index).forEach((row) => { row.type = type })
        },

        blockSelectable(index) {
            return this.blockRows(index).filter((row) => !this.problems(row).length)
        },

        blockSelected(index) {
            const rows = this.blockSelectable(index)
            return rows.length > 0 && rows.every((row) => row.selected)
        },

        blockPartial(index) {
            const rows = this.blockSelectable(index)
            return rows.some((row) => row.selected) && !rows.every((row) => row.selected)
        },

        selectBlock(index, value) {
            this.blockSelectable(index).forEach((row) => { row.selected = !!value })
        },

        // ==== Empresa e passageiro ==========================================

        customerName(id) {
            return this.customers[id] ? this.customers[id].name : ''
        },

        addressesOf(row) {
            const customer = this.customers[row.customer_id]
            return customer ? customer.addresses : []
        },

        editPassenger(row) {
            row.editingPassenger = true
            row.searchResults = []
        },

        pickCandidate(row, id) {
            const candidate = row.candidates.find((item) => item.id === id)
            if (!candidate) return
            this.applyPassenger(row, {
                id: candidate.id,
                name: candidate.name,
                address: candidate.address,
                customer_id: candidate.customer_id,
                has_location: true
            })
        },

        searchPassenger: debounce(async function (row, text) {
            if (!text || text.length < 3 || text === row.passenger_name) return

            row.searching = true
            try {
                const result = await axios.get('/api/v2/passengers/list', { params: { start: 1, limit: 10, search: { name: text } } })
                row.searchResults = result.data.data || []
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            } finally {
                row.searching = false
            }
        }, 500),

        pickPassenger(row, passenger) {
            if (!passenger) return

            const customer = passenger.customer
            if (customer && !this.customers[customer.id]) {
                this.$set(this.customers, customer.id, {
                    id: customer.id,
                    name: customer.name,
                    addresses: (customer.addresses || []).map((item) => ({ id: item.id, name: item.name || item.address, address: item.address })),
                    cost_centers: []
                })
            }

            this.applyPassenger(row, {
                id: passenger.id,
                name: passenger.name,
                address: passenger.address,
                customer_id: passenger.customer_id,
                cost_center_id: passenger.cost_center_id,
                has_location: !!(passenger.address_iid || (passenger.address_lat && passenger.address_lng))
            })
        },

        applyPassenger(row, passenger) {
            const changedCustomer = Number(row.customer_id) !== Number(passenger.customer_id)

            row.passenger_id = passenger.id
            row.passenger_name = passenger.name
            row.passenger_address = passenger.address
            row.passenger_has_location = passenger.has_location
            row.editingPassenger = false
            row.warnings = row.warnings.filter((msg) => !msg.startsWith('Nome parecido'))

            if (passenger.cost_center_id !== undefined && changedCustomer) row.cost_center_id = passenger.cost_center_id

            if (changedCustomer) {
                row.customer_id = passenger.customer_id
                row.customer_name = this.customerName(passenger.customer_id) || row.customer_name
                const addresses = this.addressesOf(row)
                row.customer_address_id = addresses.length ? addresses[0].id : null
                row.requester_id = null
            }

            row.selected = !this.problems(row).length && !row.duplicate
        },

        // ==== Criar =========================================================

        async confirmCreate() {
            const rows = this.selectedRows
            const ok = await this.$dialog.confirm({
                title: 'Criar solicitações',
                text: `Criar ${rows.length} solicitações aprovadas? Elas vão para a Logística com o carro sugerido para você gerar os horários e criar as OS.`,
                actions: { false: 'Cancelar', true: { text: 'Criar', color: 'primary' } }
            })

            if (!ok) return

            this.saving = true
            try {
                const payload = rows.map((row) => ({
                    key: row.key,
                    sheet_name: row.sheet_name,
                    type: row.type,
                    date: row.date,
                    time: row.time,
                    passenger_id: row.passenger_id,
                    customer_address_id: row.customer_address_id,
                    cost_center_id: row.cost_center_id,
                    requester_id: row.requester_id,
                    vehicle_number: row.vehicle_number || null,
                    transport: row.transport,
                    observation: row.observation,
                    allow_duplicate: row.duplicate
                }))

                const response = await axios.post('/api/v2/logistics/import', { rows: payload })
                const content = response.data.content
                const failed = new Set(content.errors.map((error) => error.key))

                // Tira da lista o que foi criado; ficam só as linhas com erro e as não selecionadas
                const sent = new Set(rows.map((row) => row.key))
                this.rows = this.rows.filter((row) => !sent.has(row.key) || failed.has(row.key))

                this.result = { show: true, created: content.created, errors: content.errors }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
            } finally {
                this.saving = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.paste-area ::v-deep textarea {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 12px;
    white-space: pre;
}

.block-header {
    background: #f3f6fb;
    padding: 8px 12px !important;
    font-size: 13px;
}

.block-type {
    max-width: 150px;
    vertical-align: middle;
}

.block-notes {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
}

.status-line {
    font-size: 12px;
    line-height: 1.5;
}

.import-table ::v-deep td {
    padding: 0 8px !important;
    white-space: normal;
    word-break: break-word;
}
</style>
